import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, EmptyState } from './Card.js';
import { fmtMonth } from '../utils.js';
import { useMoney } from '../money.js';
import { SERIES, GRID, AXIS, TOOLTIP_PROPS, VOLUME_GRADIENT, MONEY_AXIS_WIDTH } from '../chartTheme.js';
import type { PerformanceRow } from '@agio/core';

/** Asset volume over time (area) — the היקף נכסים chart. */
export function AssetVolumeChart({ perf }: { perf: PerformanceRow[] }) {
  const money = useMoney();
  const fundIds = perf.map((p) => p.fund_id);
  const data = perf
    .filter((p) => p.asset_volume !== null)
    .map((p) => ({ date: p.date, volume: p.asset_volume as number }));
  if (data.length === 0) return <Card title="היקף נכסים"><EmptyState label="אין נתונים" /></Card>;

  return (
    <Card title="היקף נכסים">
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id="vol" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={VOLUME_GRADIENT.from} stopOpacity={0.35} />
              <stop offset="100%" stopColor={VOLUME_GRADIENT.from} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID} />
          <XAxis dataKey="date" tickFormatter={fmtMonth} {...AXIS} />
          <YAxis tickFormatter={(v: number) => `${Math.round(v / 1e6)}M`} {...AXIS} width={MONEY_AXIS_WIDTH} orientation="right" />
          <Tooltip
            {...TOOLTIP_PROPS}
            formatter={(v: number) => [money(v, fundIds), 'היקף']}
            labelFormatter={fmtMonth}
          />
          <Area type="monotone" dataKey="volume" stroke={SERIES.volume} strokeWidth={2} fill="url(#vol)" />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
}

/** Deposits vs withdrawals — a scrollable table of monthly movements (newest first). */
export function NetFlowChart({ perf }: { perf: PerformanceRow[] }) {
  const money = useMoney();
  const fundIds = perf.map((p) => p.fund_id);
  const fmt = (v: number) => money(v, fundIds);
  const rows = perf
    .map((p) => ({ key: p.perf_id, date: p.date, deposits: p.deposits ?? 0, withdrawals: p.withdrawals ?? 0 }))
    .filter((p) => p.deposits !== 0 || p.withdrawals !== 0)
    .reverse(); // newest first
  if (rows.length === 0) return <Card title="הפקדות ומשיכות"><EmptyState label="אין תנועות" /></Card>;

  const totals = rows.reduce(
    (a, r) => ({ deposits: a.deposits + r.deposits, withdrawals: a.withdrawals + r.withdrawals }),
    { deposits: 0, withdrawals: 0 },
  );
  const netTotal = totals.deposits - totals.withdrawals;

  return (
    <Card title="הפקדות ומשיכות">
      <div className="max-h-[220px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border text-right text-muted-foreground">
              <th className="py-2 font-normal">חודש</th>
              <th className="py-2 text-left font-normal">הפקדות</th>
              <th className="py-2 text-left font-normal">משיכות</th>
              <th className="py-2 text-left font-normal">נטו</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const net = r.deposits - r.withdrawals;
              return (
                <tr key={r.key} className="border-b border-border/50">
                  <td className="py-2">{fmtMonth(r.date)}</td>
                  <td className="py-2 text-left tabular-nums text-success">{r.deposits ? fmt(r.deposits) : '—'}</td>
                  <td className="py-2 text-left tabular-nums text-destructive">{r.withdrawals ? fmt(r.withdrawals) : '—'}</td>
                  <td className={`py-2 text-left tabular-nums ${net >= 0 ? 'text-success' : 'text-destructive'}`}>{fmt(net)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="sticky bottom-0 bg-card font-semibold">
              <td className="py-2">סה"כ</td>
              <td className="py-2 text-left tabular-nums text-success">{fmt(totals.deposits)}</td>
              <td className="py-2 text-left tabular-nums text-destructive">{fmt(totals.withdrawals)}</td>
              <td className={`py-2 text-left tabular-nums ${netTotal >= 0 ? 'text-success' : 'text-destructive'}`}>{fmt(netTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}
